import './PromoCode.sass';

import React, { FC, useCallback, useEffect, useState } from 'react';

import { ReactComponent as TicketSVG } from './assets/ticket.svg';
import { useTranslator, useUnitName } from '$hooks';
import { BottomBar, Button, Header, Placeholder, ScrollView } from '$uikit';
import { IcLogo40 } from '$assets';
import { useRoute, useRouter } from 'react-router5';
import { Api } from '$api';
import { useDispatch, useSelector } from 'react-redux';
import { mainActions, mainSelector } from '$store/main';
import { Loader, UnitPhoto } from '$shared/components';
import {
  CHAT,
  FEED,
  SUBSCRIPTION_ADULT,
  SUBSCRIPTION_AUTHOR,
} from '$shared/constants/pages';
import { toastActions } from '$store/toast';
import { AuthPopoverSimple } from '$core/AuthPopover/AuthPopover';
import { profileActions } from '$store/profile';
import { AuthPopoverState } from '$store/models';
import { promoCodeActions } from '$store/promoCode';
import { setStorageItem, statReachGoal } from '$utils';
import { ContentPriorityType } from '$store/main/interface';

export const PromoCode: FC = () => {
  const dispatch = useDispatch();
  const t = useTranslator();
  const { route } = useRoute();
  const router = useRouter();
  const { code, v } = route.params;
  const [userId, setUserId] = React.useState(0);
  const { users } = useSelector(mainSelector);
  const user = users[userId];
  const [codeValue, setCodeValue] = useState(v || '');
  const [error, setError] = useState('');
  const userName = useUnitName(user, 'genitive');

  useEffect(() => {
    statReachGoal('promo_code_init', {
      codeId: code,
    });
    setStorageItem('contentPriorityType', ContentPriorityType.ADULT);
    Api.get('/promo_code/' + code)
      .then((data: any) => {
        dispatch(mainActions.setUsers({ [data.user.id]: data.user }));
        setUserId(data.user.id);
        statReachGoal('promo_code_open', {
          modelId: data.user.id,
          codeId: code,
          code: codeValue,
        });
      })
      .catch((error) => {
        setError(error.message);
      });
  }, [code]);

  const handleApply = useCallback(() => {
    if (!codeValue) {
      return dispatch(toastActions.setToastFail(t('promo_code_error_empty')));
    }
    dispatch(toastActions.setToastLoading());
    Api.post(`/promo_code/${code}/activate`, { code: codeValue })
      .then((data: any) => {
        router.navigate(CHAT, { peerId: userId });
        dispatch(toastActions.hideToast());
        statReachGoal('promo_code_activated', {
          modelId: userId,
          codeId: code,
          code: codeValue,
        });
        // showModal('code_content', false, {}, { content: data.content });
      })
      .catch((error) => {
        if (error.code === 'access_token') {
          dispatch(toastActions.hideToast());
          dispatch(
            promoCodeActions.setAuthCode({
              value: codeValue,
              id: code,
              modelId: userId,
            }),
          );
          dispatch(profileActions.setAuthPopoverState(AuthPopoverState.REGISTER));
        } else if (error.code === 'erotic_subscription_required') {
          router.navigate(SUBSCRIPTION_ADULT);
        } else if (error.code === 'sponsor_subscription_required') {
          router.navigate(SUBSCRIPTION_AUTHOR, { authorId: userId });
        } else if (error.code === 'code_incorrect') {
          setCodeValue('');
        }
        statReachGoal('promo_code_activate_error', {
          modelId: userId,
          codeId: code,
          code: codeValue,
          errorCode: error.code,
        });
        dispatch(toastActions.setToastFail(error.message));
      });
  }, [codeValue, code, userId]);

  return (
    <>
      <Header>
        <div className="PromoCode__header">
          <IcLogo40 />
          <span>Ton.Place</span>
        </div>
      </Header>
      {user ? (
        <>
          <ScrollView>
            <div className="PromoCode__content">
              <UnitPhoto size="big" unit={user} nftIndicator={false} />
              <div className="PromoCode__content__title">
                {t('promo_code_author_title', { userName })}
              </div>
              <div className="PromoCode__content__desc">
                {t('promo_code_author_desc')}
              </div>
              <div className="PromoCode__content__input">
                <TicketSVG />
                <input
                  type="text"
                  placeholder={t('promo_code_input_placeholder')}
                  value={codeValue}
                  onChange={(e) => {
                    setCodeValue(e.target.value);
                  }}
                />
              </div>
            </div>
          </ScrollView>
          <BottomBar>
            <Button onClick={handleApply} stretched>
              {t('promo_code_activate_button')}
            </Button>
          </BottomBar>
        </>
      ) : error ? (
        <ScrollView>
          <Placeholder
            centered
            title={
              error === 'error_promo_code_not_found'
                ? t('promo_code_error_not_found')
                : error
            }
          >
            <Button
              size="medium"
              stretched
              onClick={() => {
                router.navigate(FEED);
              }}
            >
              {t('promo_code_error_code_not_found_button')}
            </Button>
          </Placeholder>
        </ScrollView>
      ) : (
        <Loader isWrapped />
      )}
      <AuthPopoverSimple />
    </>
  );
};
